import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import LoginModal from './LoginModal';
import { openCustomerPortal } from '../../utils/cn';
import { canonicalPath } from '../../lib/seo.mjs';


const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [expandedMobileItem, setExpandedMobileItem] = useState(null);
  const mobileMenuRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const primaryNavItems = [
    { name: 'Home', path: '/' },
    { name: 'How It Works', path: '/how-it-works' },
    // Quote Check traffic CTA — promoted out of the "More" dropdown (where it
    // lived as "Compare a Quote") into the top-level nav. Also reaches the
    // mobile drawer automatically via allMobileItems below.
    { name: 'Free Quote Check', path: '/compare/quote' },
    { name: 'Financing', path: '/financing-options' },
    { name: 'About', path: '/about' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Insurance', path: '/insurance' }
  ];

  const secondaryNavItems = [
    { name: 'Gallery', path: '/gallery' },
    { name: 'Shingles', path: '/shingles' },
    { name: 'Warranty', path: '/warranty' },
    { name: 'Service Areas', path: '/service-areas', submenu: [
      { name: 'Dayton, OH', path: '/service-areas/ohio/dayton' },
      { name: 'Columbus, OH', path: '/service-areas/ohio/columbus' },
      { name: 'Cincinnati, OH', path: '/service-areas/ohio/cincinnati' },
      { name: 'Dallas, TX', path: '/service-areas/texas/dallas' },
      { name: 'View All Service Areas', path: '/service-areas' }
    ]},
    { name: 'For Installers', path: '/for-contractors' },
    { name: 'Support', path: '/support' },
    { name: 'Blog', path: '/blog' },
  ];

  const isActivePath = (path) => {
    // Compare NORMALIZED paths. The nav entries above are bare
    // ('/how-it-works') but a visitor who lands on a prerendered page is at
    // the trailing-slash URL ('/how-it-works/') — nginx 301s the bare path
    // because prerendered routes are directories (docs/url-conventions.md).
    // A raw === therefore went false and the active nav state silently
    // disappeared on every marketing page entered directly. Worse, the
    // prerenderer's StaticRouter renders at the BARE path, so the shipped
    // HTML said "active" while the hydrating client said "inactive" — a
    // hydration mismatch of the same family as the one fixed in ebb15a1.
    const here = canonicalPath(location?.pathname || '/');
    if (path === '/homepage' && here === '/') return true;
    return here === canonicalPath(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setExpandedMobileItem(null);
  };

  const allMobileItems = [...primaryNavItems, ...secondaryNavItems];

  return (
    <>
      {/* Floating Header Container.
          padding-top combines safe-area-inset-top (iOS notch / status
          bar zone) WITH the existing breathing-room padding via Tailwind
          arbitrary calc. On devices without notches (desktop, older
          phones), env(safe-area-inset-top) resolves to 0 so behavior is
          unchanged. On notched iPhones, the pill is pushed below the
          iOS status bar so iOS's translucent status-bar overlay doesn't
          render on top of the white pill (which had been showing as a
          "translucent grey band with page content showing through"). */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-8 pt-[calc(env(safe-area-inset-top)+1rem)] sm:pt-[calc(env(safe-area-inset-top)+1.25rem)] lg:pt-[calc(env(safe-area-inset-top)+1.5rem)]">
        {/* Floating Glass Pill/Oval */}
        <div 
          className="mx-auto max-w-[98%] lg:max-w-[94%] xl:max-w-[92%] transition-all duration-500 backdrop-blur-md"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '9999px',
            boxShadow: isScrolled 
              ? '0 8px 32px rgba(0, 0, 0, 0.05), 0 2px 8px rgba(0, 0, 0, 0.03)'
              : '0 4px 24px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}
        >
          <div className="flex items-center justify-between h-24 sm:h-18 lg:h-20 xl:h-22 pl-3 pr-4 sm:pl-4 sm:pr-6 lg:pl-5 lg:pr-8 xl:pl-6 xl:pr-10">
            {/* Logo with enhanced effects */}
            <Link 
              to="/" 
              className="relative flex items-center group transition-all duration-500 hover:scale-105 touch-manipulation z-10"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-150" />
              {/* Logo height bumped again 2026-05-24 (round 2) — mobile
                  brand mark to h-20 (80px) inside an h-24 (96px) pill,
                  8px breathing each side. Mobile now matches lg desktop
                  logo size. Desktop sm/lg/xl unchanged. */}
              <img
                src="/assets/images/Openbooq_blue-1773069231613.png"
                alt="OpenbooQ brand logo featuring modern 3D interlocking geometric design with circular cylindrical shapes in gradient blue, cyan and gray tones"
                className="relative h-20 sm:h-16 lg:h-20 xl:h-24 w-auto object-contain transition-all duration-500 group-hover:brightness-110 group-hover:drop-shadow-2xl"
              />
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500" />
            </Link>

            {/* Desktop Navigation with enhanced styling.
                Pass 1 (2026-05-24): font-size bumped 11.5/12.5 → 13.5/14.5
                and inter-item spacing space-x-1/2 → space-x-2/3 so links
                read clearly and breathe. Still smaller than the CTA so
                "Get Instant Estimate" remains the single primary action. */}
            <nav className="hidden lg:flex items-center space-x-2 xl:space-x-3">
              {primaryNavItems?.map((item, index) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`relative px-3 xl:px-4 py-2 text-[13.5px] xl:text-[14.5px] font-poppins transition-all duration-300 rounded-xl group ${
                    isActivePath(item?.path)
                      ? 'text-white font-semibold' : 'text-[#0F172A] hover:text-blue-600 font-medium'
                  }`}
                  style={{ fontWeight: isActivePath(item?.path) ? 600 : 500 }}
                >
                  {/* Active background with solid blue #07b0e9 */}
                  {isActivePath(item?.path) && (
                    <>
                      <span className="absolute inset-0 rounded-xl" style={{ backgroundColor: '#07b0e9' }} />
                      <span className="absolute inset-0 rounded-xl" style={{ boxShadow: '0 0 20px rgba(7, 176, 233, 0.4)' }} />
                    </>
                  )}
                  
                  {/* Hover effect */}
                  {!isActivePath(item?.path) && (
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                  
                  <span className="relative z-10 flex items-center">
                    {item?.name}
                    {isActivePath(item?.path) && (
                      <span className="ml-2 w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                    )}
                  </span>
                  
                  {/* Animated underline with glow */}
                  <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-300 ${
                    isActivePath(item?.path) 
                      ? 'w-8 bg-white/50' :'w-0 bg-gradient-to-r from-blue-500 to-cyan-500 group-hover:w-8'
                  }`} />
                </Link>
              ))}
              
              {/* More Dropdown — Pass 1 (2026-05-24): font/weight unified
                  with primary nav links (13.5/14.5, font-medium instead
                  of font-semibold) so "More" sits visually flush with the
                  other items rather than dominating them. */}
              <div className="relative group">
                <button className="flex items-center space-x-1.5 px-3 xl:px-4 py-2 font-poppins font-medium text-[13.5px] xl:text-[14.5px] tracking-tight text-[#0F172A] hover:text-primary hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 rounded-xl relative overflow-hidden">
                  <span className="relative z-10">More</span>
                  <Icon name="ChevronDown" size={16} className="relative z-10 transition-transform duration-500 group-hover:rotate-180" />
                  <span className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
                
                <div className="absolute top-full right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  {/* Gradient border effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-blue-500/10 rounded-2xl" />
                  
                  <div className="relative py-2">
                    {secondaryNavItems?.map((item, index) => (
                      item?.submenu ? (
                        <div key={item?.path} className="relative group/sub">
                          <div
                            className={`flex px-5 py-3.5 text-[15px] font-inter font-medium transition-all duration-200 relative cursor-pointer ${
                              isActivePath(item?.path)
                                ? 'text-primary bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-primary' :'text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-cyan-50/50'
                            } ${
                              index !== secondaryNavItems?.length - 1 ? 'border-b border-gray-50' : ''
                            }`}
                          >
                            <span className="flex items-center justify-between relative z-10 w-full">
                              <span className="flex items-center">
                                {item?.name}
                                {isActivePath(item?.path) && (
                                  <span className="ml-2 w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                                )}
                              </span>
                              <Icon name="ChevronRight" size={16} className="text-gray-400 transition-transform duration-200 group-hover/sub:text-primary group-hover/sub:translate-x-0.5" />
                            </span>
                            {!isActivePath(item?.path) && (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-20 group-hover/sub:w-1 transition-all duration-300 rounded-r" />
                            )}
                          </div>
                          {/* Nested submenu */}
                          <div className="absolute left-full top-0 ml-1 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover/sub:opacity-100 group-hover/sub:visible transition-all duration-200 transform translate-x-1 group-hover/sub:translate-x-0 z-50 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-cyan-500/5 to-blue-500/5 rounded-xl" />
                            <div className="relative py-1.5">
                              {item?.submenu?.map((sub, subIndex) => (
                                <Link
                                  key={sub?.path}
                                  to={sub?.path}
                                  className={`block px-4 py-3 text-[14px] font-inter font-medium transition-all duration-200 relative group/leaf ${
                                    isActivePath(sub?.path)
                                      ? 'text-primary bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-primary' :'text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-cyan-50/50'
                                  } ${
                                    subIndex !== item?.submenu?.length - 1 ? 'border-b border-gray-50' : ''
                                  }`}
                                >
                                  <span className="flex items-center justify-between relative z-10">
                                    <span className="flex items-center gap-2">
                                      {sub?.name === 'Dayton, OH' && (
                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#07B0E9' }} />
                                      )}
                                      {sub?.name}
                                    </span>
                                    <Icon name="ChevronRight" size={14} className="text-gray-300 opacity-0 group-hover/leaf:opacity-100 group-hover/leaf:text-primary transition-all duration-200" />
                                  </span>
                                  {!isActivePath(sub?.path) && (
                                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0 h-7 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-20 group-hover/leaf:w-1 transition-all duration-300 rounded-r" />
                                  )}
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Link
                          key={item?.path}
                          to={item?.path}
                          className={`block px-5 py-3.5 text-[15px] font-inter font-medium transition-all duration-200 relative group/item ${
                            isActivePath(item?.path) 
                              ? 'text-primary bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-primary' :'text-gray-700 hover:text-primary hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-cyan-50/50'
                          } ${
                            index !== secondaryNavItems?.length - 1 ? 'border-b border-gray-50' : ''
                          }`}
                        >
                          <span className="flex items-center justify-between relative z-10">
                            <span className="flex items-center">
                              {item?.name}
                              {isActivePath(item?.path) && (
                                <span className="ml-2 w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                              )}
                            </span>
                            {isActivePath(item?.path) ? (
                              <Icon name="Check" size={16} className="text-primary" />
                            ) : (
                              <Icon name="ChevronRight" size={16} className="text-gray-400 opacity-0 group-hover/item:opacity-100 transition-opacity duration-200" />
                            )}
                          </span>
                          {!isActivePath(item?.path) && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-20 group-hover/item:w-1 transition-all duration-300 rounded-r" />
                          )}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            {/* CTA Buttons with enhanced styling */}
            <div className="hidden lg:flex items-center space-x-3 xl:space-x-4">
              <button
                onClick={() => { openCustomerPortal(); }}
                className="relative overflow-hidden font-semibold px-3 xl:px-4 py-1.5 text-[13px] xl:text-[14px] rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-blue-700/20"
                style={{ backgroundColor: '#07b0e9', color: 'white' }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <span>Get Your Price</span>
                </span>
              </button>
              
              {/* Log In / Get Started — Pass 1 (2026-05-24): de-emphasized
                  so the CTA pill is the single primary action. Color
                  shifted from #0F172A → gray-500, weight dropped to
                  font-normal, icon size 15 → 13, and the hover-gradient
                  bg removed. Now reads as a tertiary text link. Function
                  unchanged. */}
              <button
                onClick={() => setIsLoginModalOpen(true)}
                className="relative group px-2 xl:px-3 py-1.5 font-inter font-normal text-[12.5px] xl:text-[13.5px] text-gray-500 hover:text-gray-700 transition-colors duration-300 rounded-lg flex items-center gap-1.5"
              >
                <Icon name="User" size={13} className="transition-transform duration-300 group-hover:scale-110" />
                <span>Log In / Get Started</span>
              </button>
            </div>

            {/* Mobile Buttons Container - Login button to the left of Menu button */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Mobile Login Button — slimmed 2026-05-24: text shortened
                  from "Log In / Get Started" to "Log In" (the latter
                  framing exists for the "Get Started" intent on the
                  hero/hamburger CTAs), padding tightened to px-2.5 py-1.5,
                  icon size 15 → 14. Net result: button is roughly half
                  its previous width, giving the logo more room and the
                  hamburger more breathing space in the pill. */}
              <button
                onClick={() => setIsLoginModalOpen(true)}
                className="px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 active:scale-95 transition-all duration-300 touch-manipulation flex items-center justify-center gap-1 shadow-md hover:shadow-lg relative group overflow-hidden"
                aria-label="Log in to your account"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Icon name="User" size={14} className="relative z-10 text-white" />
                <span className="relative z-10 text-white font-semibold text-xs whitespace-nowrap">Log In</span>
              </button>

              {/* Mobile Hamburger Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="p-3 sm:p-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 active:scale-95 transition-all duration-300 touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center relative group overflow-hidden"
                aria-label="Toggle mobile menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative w-6 h-6 flex items-center justify-center z-10">
                  {/* Animated Hamburger Icon with gradient */}
                  <span className={`absolute w-6 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 transition-all duration-300 ease-in-out rounded-full ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'
                  }`} />
                  <span className={`absolute w-6 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 transition-all duration-300 ease-in-out rounded-full ${
                    isMobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
                  }`} />
                  <span className={`absolute w-6 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 transition-all duration-300 ease-in-out rounded-full ${
                    isMobileMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'
                  }`} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Mobile Menu Overlay with enhanced blur */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-gradient-to-b from-black/60 to-black/40 backdrop-blur-md z-40 transition-all duration-300 animate-fadeIn"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
      {/* Mobile Menu Drawer - Enhanced with gradient.
          The closed-state translation is `-translate-y-[calc(100%+5rem)]`
          (NOT plain `-translate-y-full`) — that was the source of the
          "translucent grey band with text behind the header" reported
          2026-05-24. Layout box: `top-20 bottom-0` → height =
          viewport - 80px. `-translate-y-full` shifts only by that
          height, leaving the drawer's bottom 80px (which corresponds
          to the `top-20` offset) STILL VISIBLE at the top of the
          screen. Worse, since the drawer is scrollable (overflow-y-auto)
          and contains the full nav list including "For Installers,"
          the bottom of that list visibly peeked through as the user
          scrolled the page. Adding +5rem (= the 80px top-20 offset)
          to the translation pushes the drawer's bottom edge below
          y=0, fully off-screen. The slide-down animation still works
          on open; just the travel distance is slightly longer. */}
      <div
        ref={mobileMenuRef}
        className={`lg:hidden fixed top-20 left-0 right-0 bottom-0 z-40 bg-gradient-to-b from-white via-blue-50/30 to-white transform transition-transform duration-500 ease-out overflow-y-auto ${
          isMobileMenuOpen ? 'translate-y-0' : '-translate-y-[calc(100%+5rem)]'
        }`}
      >
        <div className="px-4 sm:px-6 py-6">
          <nav className="space-y-2">
            {allMobileItems?.map((item, index) => (
              item?.submenu ? (
                <div key={item?.path}>
                  {/* Expandable parent item */}
                  <button
                    onClick={() => setExpandedMobileItem(expandedMobileItem === item?.path ? null : item?.path)}
                    className={`w-full px-5 py-4 font-inter font-semibold text-base rounded-xl transition-all duration-300 touch-manipulation min-h-[56px] flex items-center justify-between relative overflow-hidden group ${
                      isActivePath(item?.path)
                        ? 'text-white shadow-lg'
                        : 'text-gray-700 hover:text-primary active:bg-gray-100'
                    }`}
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    {isActivePath(item?.path) && (
                      <>
                        <span className="absolute inset-0 bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 animate-gradient-x" />
                        <span className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 blur-xl" />
                      </>
                    )}
                    {!isActivePath(item?.path) && (
                      <span className="absolute inset-0 bg-gradient-to-r from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    )}
                    <span className="relative z-10">{item?.name}</span>
                    <Icon
                      name="ChevronDown"
                      size={20}
                      className={`relative z-10 text-gray-400 transition-transform duration-300 ${
                        expandedMobileItem === item?.path ? 'rotate-180 text-primary' : ''
                      }`}
                    />
                  </button>
                  {/* Submenu items */}
                  {expandedMobileItem === item?.path && (
                    <div className="mt-1 ml-4 space-y-1">
                      {item?.submenu?.map((sub) => (
                        <Link
                          key={sub?.path}
                          to={sub?.path}
                          onClick={closeMobileMenu}
                          className={`flex items-center gap-3 px-5 py-3.5 font-inter font-medium text-sm rounded-xl transition-all duration-300 touch-manipulation min-h-[48px] relative overflow-hidden group ${
                            isActivePath(sub?.path)
                              ? 'text-white shadow-md'
                              : 'text-gray-600 hover:text-primary'
                          }`}
                        >
                          {isActivePath(sub?.path) && (
                            <span className="absolute inset-0 rounded-xl" style={{ backgroundColor: '#07b0e9' }} />
                          )}
                          {!isActivePath(sub?.path) && (
                            <span className="absolute inset-0 bg-gradient-to-r from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
                          )}
                          <span className="relative z-10 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#07B0E9' }} />
                          <span className="relative z-10 flex-1">{sub?.name}</span>
                          <Icon name="ChevronRight" size={16} className="relative z-10 text-gray-400 group-hover:text-primary transition-colors duration-300" />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMobileMenu}
                  className={`block px-5 py-4 font-inter font-semibold text-base rounded-xl transition-all duration-300 touch-manipulation active:scale-98 min-h-[56px] flex items-center justify-between relative overflow-hidden group ${
                    isActivePath(item?.path) 
                      ? 'text-white shadow-lg' 
                      : 'text-gray-700 hover:text-primary active:bg-gray-100'
                  }`}
                  style={{
                    animationDelay: `${index * 30}ms`
                  }}
                >
                  {isActivePath(item?.path) && (
                    <>
                      <span className="absolute inset-0 bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 animate-gradient-x" />
                      <span className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 blur-xl" />
                    </>
                  )}
                  {!isActivePath(item?.path) && (
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  )}
                  <span className="relative z-10">{item?.name}</span>
                  <span className="relative z-10">
                    {isActivePath(item?.path) ? (
                      <Icon name="Check" size={20} className="text-white" />
                    ) : (
                      <Icon name="ChevronRight" size={20} className="text-gray-400 group-hover:text-primary transition-colors duration-300" />
                    )}
                  </span>
                </Link>
              )
            ))}
          </nav>
          
          {/* Mobile CTA Buttons - Enhanced */}
          <div className="mt-8 pt-6 border-t border-gray-200 space-y-4">
            <Button 
              variant="outline" 
              fullWidth
              className="relative font-semibold text-base py-4 min-h-[56px] border-2 border-gray-300 hover:border-primary active:scale-98 transition-all duration-300 touch-manipulation group overflow-hidden rounded-xl"
              onClick={() => {
                setIsLoginModalOpen(true);
                closeMobileMenu();
              }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative z-10 flex items-center justify-center">
                <Icon name="User" size={20} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                Log In
              </span>
            </Button>
            <Button 
              variant="default" 
              fullWidth
              className="relative font-semibold text-base py-4 min-h-[56px] bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 shadow-xl hover:shadow-2xl active:scale-98 transition-all duration-300 touch-manipulation group overflow-hidden rounded-xl animate-gradient-x"
              onClick={() => {
                window.location.href = '/instant-estimate';
                closeMobileMenu();
              }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-50 blur-xl" />
              <span className="relative z-10 flex items-center justify-center">
                Get Started
                <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Button>
          </div>

          {/* Additional Touch-Friendly Spacing */}
          <div className="h-24" />
        </div>
      </div>
      {/* Mobile Sticky CTA Bar - Enhanced */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-white via-white to-white/95 border-t border-gray-200 p-4 shadow-2xl safe-area-inset-bottom backdrop-blur-sm">
        <Button 
          variant="default" 
          fullWidth
          size="lg"
          className="relative font-bold text-base py-4 min-h-[56px] bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 shadow-xl hover:shadow-2xl active:scale-98 transition-all duration-300 touch-manipulation group overflow-hidden rounded-xl animate-gradient-x"
          onClick={() => { setIsMobileMenuOpen(false); openCustomerPortal(); }}
        >
          <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-active:opacity-100 transition-opacity duration-200" />
          <span className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 opacity-50 blur-xl" />
          <span className="relative z-10 flex items-center justify-center">
            Get Started - See Your Real Price
            <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
          </span>
        </Button>
      </div>
      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </>
  );
};

export default Header;